import { useRef, useState } from 'react'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { NavLink } from 'react-router-dom'
import { Button } from '../../../components/button'
import { ChartCard } from '../../../components/cards/ChartCard'
import DoughnutChart, {
  CustomLegend,
} from '../../../components/charts/DoughnutChart'
import Icon from '../../../components/icon'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import { ExportDataModal } from '../../../components/modal/app_modals/exportDataModal'
import { TableComponent } from '../../../components/table'
import {
  formatCurrency,
  getPageFromSkip,
  getSkipFromPage,
} from '../../../helpers'
import { useAppSelector } from '../../../hooks'
import { useModalControl } from '../../../hooks/useModalControl'
import { useGetSalesRepReportQuery } from '../../../redux-toolkits/reports/reports.slice'
import { DashboardReportParams } from '../../../redux-toolkits/reports/reports.type'
import { SalesRepReportQuery } from '../../../redux-toolkits/reports/reports.type'
export const SalesRepReportView = ({
  mainDashQuery,
}: {
  mainDashQuery: DashboardReportParams
}) => {
  const [query, setQuery] = useState<SalesRepReportQuery>({
    filter: 'today',
    isCustomDateRange: false,
    startDate: undefined,
    endDate: undefined,
    skip: 0,
    limit: 20,
    sortOrder: 'desc',
    fulfilmentCentre: undefined,
  })
  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { data: respData, isFetching: fetchingData } =
    useGetSalesRepReportQuery(
      {
        ...query,
        ...mainDashQuery,
        search: query.search === '' ? undefined : query.search,
        fulfilmentCentre:
          selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const chartRef = useRef<ChartJSOrUndefined<'doughnut'>>(null)

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Active Sales Reps">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="mt-4 w-[214px] h-[214px] relative">
              <p className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold">
                  {respData?.data?.summary?.totalSalesReps ?? 0}
                </span>
                <span className="text-sec-black text-sm">total sales reps</span>
              </p>
              <DoughnutChart
                labels={['Active Sales Reps', 'Inactive Sales Reps']}
                bgColor={['#1B9066', '#CA3542']}
                data={[
                  respData?.data?.summary?.activeSalesReps ?? 0,
                  respData?.data?.summary?.inactiveSalesReps ?? 0,
                ]}
                height={50}
                width={50}
                responsive
                hoverLabel="Count"
                chartRef={chartRef}
                customLegend={true}
              />
            </div>

            <div className="flex-1 gap-4 flex flex-col">
              <p className="flex items-center gap-2">
                <Icon
                  id="customers-icon"
                  width={24}
                  height={24}
                  className="text-[#1B9066]"
                />
                <span className="text-xs text-sec-black">
                  {(
                    respData?.data?.summary?.activeSalesReps ?? 0
                  ).toLocaleString()}{' '}
                  sales rep(s) are active
                </span>
              </p>

              <p className="flex items-center gap-2">
                <Icon
                  id="customers-icon"
                  width={24}
                  height={24}
                  className="text-[#CA3542]"
                />
                <span className="text-xs text-sec-black">
                  {(
                    respData?.data?.summary?.inactiveSalesReps ?? 0
                  ).toLocaleString()}{' '}
                  sales rep(s) are inactive
                </span>
              </p>

              <p className="flex items-center gap-2">
                <Icon
                  id="customers-icon"
                  width={24}
                  height={24}
                  className="text-amber"
                />
                <span className="text-xs text-sec-black">
                  {(
                    respData?.data?.summary?.salesRepsWithSales ?? 0
                  ).toLocaleString()}{' '}
                  sales rep(s) made sales
                </span>
              </p>
            </div>
          </div>

          <CustomLegend chartRef={chartRef} className="mt-10" />
        </ChartCard>
      </div>

      <div className=" flex-wrap flex items-center gap-4 justify-between">
        <div className="flex-1 min-w-full md:min-w-fit">
          <SearchInput
            placeholder="Search"
            searchFunction={(val) => {
              setQuery({
                ...query,
                search: val,
              })
            }}
            className="!w-full md:!max-w-[400px]"
          />
        </div>

        <Button
          label="Export"
          onClick={async () => {
            handleModalOpen('exportDataModal')
          }}
          loading={false}
          primary
          className="w-full max-w-[129px]"
        />

        <Dropdown
          className="!max-h-[58px]"
          menuButton={
            <button type="button" className="rotate-90">
              <Icon width={58} height={56} id="verticalDots" />
            </button>
          }
          menuItems={[
            {
              name: 'Total Sales: low to high',
              value: { order: 'asc', by: 'totalSales' },
            },
            {
              name: 'Total Sales: high to low',
              value: { order: 'desc', by: 'totalSales' },
            },
            {
              name: 'Total Orders: low to high',
              value: { order: 'asc', by: 'totalOrders' },
            },
            {
              name: 'Total Orders: high to low',
              value: { order: 'desc', by: 'totalOrders' },
            },
          ].map((filter) => ({
            ...filter,
            onClick: () => {
              setQuery({
                ...query,
                sortBy: filter.value.by,
                sortOrder: filter.value.order,
              })
            },
          }))}
          menuItemClassName="border-b-0"
          menuClassName="!w-[200px]"
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'NAME',
            'NO OF ORDERS',
            'TOTAL SALES (₦)',
            'AVERAGE ORDER VALUE',
            'TOTAL SIGNUPS',
            'Onboarded',
            'TOTAL FIRST PURCHASES',
            // 'LOCATION',
          ]}
          rows={
            respData?.data?.records?.map((summary, idx) => {
              return {
                id: idx,
                content: [
                  <NavLink
                    to={`/sales-rep/${summary._id}`}
                    className={`flex gap-2 items-center hover:underline capitalize`}
                    key={`sales-rep-${summary._id}-name`}
                  >
                    {summary.name}
                  </NavLink>,
                  summary.totalOrders,
                  formatCurrency(summary.totalSales),
                  formatCurrency(summary.averageOrderValue),
                  summary.accountsCreated,
                  summary.onboardedUsers,
                  summary.usersWithPurchase,
                  // 'N/A',
                ],
              }
            }) ?? []
          }
          isEmpty={[0, undefined].includes(respData?.data?.records?.length)}
          loading={fetchingData}
          currentPage={getPageFromSkip(query.skip!, query.limit!)}
          totalDataCount={respData?.data?.count}
          pageLimit={Number(query.limit!)}
          onPageChange={(page) => {
            setQuery({
              ...query,
              skip: getSkipFromPage(page, query.limit!),
            })
          }}
        />
      </div>

      <ExportDataModal
        fulfilmentCentre={
          selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined
        }
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{}}
        title={`Export Sales Representatives`}
        reportType="sales_rep_analytics"
      />
    </>
  )
}
